/**
 * Product card star ratings, filtered to 4★+ reviews.
 *
 * The Shopify `reviews.rating` metafield only carries Judge.me's unfiltered
 * average, and Liquid cannot recompute it. Judge.me exposes no bulk endpoint,
 * so each card fetches its own product's histogram lazily (only once it is
 * about to scroll into view) and recomputes the average over 4★ and 5★ ratings
 * only — the same rule snippets/product-modal-multi-img.liquid applies — so a
 * card and its quick-view modal always agree.
 *
 * Cards render hidden (see snippets/product-card-rating.liquid) and are only
 * revealed once the filtered count clears the minimum, so nothing flashes the
 * unfiltered average and no card shows an unfairly small sample.
 *
 * Keep these constants in sync with QV_REVIEWS in product-modal-multi-img.liquid
 * and the JM_* constants in cProductAddModal.vue.
 */
(function () {
  'use strict';

  var MIN_STAR = 4;        // only these ratings count toward the displayed average
  var MIN_RATINGS = 5;     // below this the card shows no rating at all
  var MIN_COLLECTIVE_AVG = 4; // collective average across ALL reviews must clear this
  var CACHE_KEY = 'jm_card_ratings_v1';
  var CACHE_TTL = 6 * 60 * 60 * 1000; // 6h — review counts move slowly
  var SHOP = (window.Shopify && window.Shopify.shop) || 'inspiredgo.myshopify.com';

  var cache = {};
  try {
    var stored = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
    if (stored && stored.t && (Date.now() - stored.t) < CACHE_TTL) cache = stored.d || {};
  } catch (e) { /* private mode / quota — fall back to in-memory only */ }

  var persistQueued = false;
  function persist() {
    if (persistQueued) return;
    persistQueued = true;
    setTimeout(function () {
      persistQueued = false;
      try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), d: cache })); } catch (e) {}
    }, 500);
  }

  // Count + average across 4★ and 5★ only, from Judge.me's per-star histogram.
  function displayStats(data) {
    var hist = (data && data.histogram) || [];
    var count = 0, weighted = 0;
    for (var i = 0; i < hist.length; i++) {
      var rating = Number(hist[i].rating);
      var freq = Number(hist[i].frequency) || 0;
      if (rating >= MIN_STAR) { count += freq; weighted += rating * freq; }
    }
    return {
      count: count,
      average: count ? weighted / count : 0,
      // Collective average across every review, used only as a gate
      collective: parseFloat(data && data.average_rating) || 0
    };
  }

  // Matches the rounding the Liquid card markup used for the half-star sprite
  function ratingDecimal(value) {
    var decimal = value % 1;
    if (decimal >= 0.3 && decimal <= 0.7) return 0.5;
    if (decimal > 0.7) return 1;
    return 0;
  }

  function paint(el, stats) {
    // Stays hidden unless the sample is big enough AND the product is
    // collectively rated above the floor
    if (stats.count < MIN_RATINGS) return;
    if ((stats.collective || 0) < MIN_COLLECTIVE_AVG) return;
    var max = el.getAttribute('data-jm-rating-max') || 5;
    var value = stats.average;
    var display = value.toFixed(1);

    var star = el.querySelector('.rating-star');
    if (star) {
      star.style.setProperty('--rating', Math.floor(value));
      star.style.setProperty('--rating-max', max);
      star.style.setProperty('--rating-decimal', ratingDecimal(value));
    }

    var wrapper = el.querySelector('.rating');
    var label = el.getAttribute('data-jm-label');
    if (wrapper && label) wrapper.setAttribute('aria-label', label.replace('__RATING__', display));

    var valueEl = el.querySelector('[data-jm-value]');
    if (valueEl) valueEl.textContent = display + ' / ' + max;

    var countEl = el.querySelector('[data-jm-count]');
    if (countEl) countEl.textContent = '(' + stats.count + ')';

    var countA11y = el.querySelector('[data-jm-count-a11y]');
    if (countA11y) countA11y.textContent = stats.count + ' ' + (el.getAttribute('data-jm-count-label') || '');

    el.hidden = false;
  }

  var inFlight = {};

  function hydrate(el) {
    var id = el.getAttribute('data-jm-product-id');
    if (!id || el.hasAttribute('data-jm-done')) return;
    el.setAttribute('data-jm-done', '');

    if (cache[id]) { paint(el, cache[id]); return; }

    if (!inFlight[id]) {
      var url = 'https://api.judge.me/reviews/reviews_for_widget'
        + '?product_id=' + encodeURIComponent(id)
        + '&shop_domain=' + encodeURIComponent(SHOP)
        + '&platform=shopify&per_page=1&page=1';
      inFlight[id] = fetch(url)
        .then(function (res) { return res.ok ? res.json() : Promise.reject(res.status); })
        .then(function (data) {
          cache[id] = displayStats(data);
          persist();
          return cache[id];
        });
    }

    inFlight[id]
      .then(function (stats) { paint(el, stats); })
      .catch(function () { /* leave the card rating hidden on failure */ });
  }

  var observer = ('IntersectionObserver' in window)
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          hydrate(entry.target);
        });
      }, { rootMargin: '200px 0px' })
    : null;

  function scan(root) {
    var nodes = (root || document).querySelectorAll('.jm-card-rating:not([data-jm-done])');
    for (var i = 0; i < nodes.length; i++) {
      if (observer) observer.observe(nodes[i]);
      else hydrate(nodes[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }

  // Grids paginate and filter in place, so pick up cards added after load
  if (window.MutationObserver) {
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes.length) { scan(); return; }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
